import {
    ActivityConductedByTypeEnumDesc, ActivityRelationItemModel,
    AFIActivitiesPerformedByOptionsEnumDesc,
    AFISupportingActivityEvidenceAvailableTypeEnumDesc,
    SupportingActivityItemModel,
} from '../../../api/models';
import { FieldArrayModel, FieldModel } from '../../../components/form/pageForm/types';
import { CheckboxGroupProps } from '../../../components/input/checkboxGroup/types';
import { NestedListDetailsProp } from '../../../components/wizardLayout/wizardNestedListContent/types';
import { yesNoRadioButtonProps, yesRspYesCrcNoRadioButtonProps } from '../../../FormDefinitions/CommonComponentProps';

const afiAppGuidanceText: FieldModel<SupportingActivityItemModel> = {
    component: 'globalContent',
    label: '',
    name: 'afiAppGuidanceText',
    contentKey: ['AFISuppActivityAppGuidanceText'],
};

const isIntentionPreviouslyRegisteredSupportingRNDActivityApplication: FieldModel<SupportingActivityItemModel> = yesNoRadioButtonProps(
    'isIntentionPreviouslyRegisteredSupportingRNDActivityApplication',
    'Is it your intention to list a previously registered supporting R&D activity for this application?',
    []);

const name: FieldModel<SupportingActivityItemModel> = {
    component: 'textInput',
    label: 'Name for this supporting R&D activity',
    name: 'name',
    contentKey: ['AFISupportingActivityName'],
    componentProps: {
        contentClassname: 'nestedListNumberLatin',
    },
};

const otherOrganisation: FieldModel<SupportingActivityItemModel> = yesRspYesCrcNoRadioButtonProps(
    'otherOrganisation',
    'Was some or all of this supporting R&D activity conducted by a Research Service Provider or Cooperative Research Centre?',
    ['supportingActivityOtherOrganisation']);

const otherOrganisationName: FieldModel<SupportingActivityItemModel> = {
    component: 'selectInput',
    label: 'Label not used check out: src\labelFunctions\RnDActivitiesForm\supportingActivities.ts',
    name: 'otherOrganisationName',
    contentKey: ['SupportingActivityWhichRspCrc'],
    componentProps: {
        toggleName: 'Please choose...',
    },
};

const briefDescription: FieldModel<SupportingActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Label not used check out: src\labelFunctions\RnDActivitiesForm\supportingActivities.ts',
    name: 'briefDescription',
    // This content key will be replaced - check out: src\labelFunctions\RnDActivitiesForm\supportingActivities.ts'
    contentKey: ['supportingActivityBriefDescription'],
    componentProps: {
        maxCharacters: 2000,
    },
};

const activityId: FieldModel<ActivityRelationItemModel> = {
    component: 'selectInput',
    label: 'Core activity',
    name: 'activityId',
    componentProps: {
        toggleName: 'Please choose...',
    },
};

const activityRelations: FieldArrayModel<ActivityRelationItemModel> = {
    addButtonTitle: 'Add another core activity',
    deleteButtonTitle: 'Delete this core activity',
    alwaysShowTheFirstRecord: true,
    showDeleteButtonOnFirstRecord: true,
    title: 'Which core R&D activities are supported by this supporting R&D activity?  ',
    contentKey: ['AFIActivitySupportsWhichCore'],
    name: 'activityRelations',
    fields: {
        activityId,
    },
};

const duration: FieldModel<SupportingActivityItemModel> = {
    component: 'monthYearRangeInput',
    label: 'When was this supporting R&D activity conducted?',
    name: 'duration',
    contentKey: ['AFISupportingActivityDates'],
};

const excluded: FieldModel<SupportingActivityItemModel> = yesNoRadioButtonProps(
    'excluded',
    'Does this supporting R&D activity fall into one of the categories that exclude it from being a core R&D activity?',
    ['AFISupportingActivityExcludedCore']);

const supportCoreActivity: FieldModel<SupportingActivityItemModel> = yesNoRadioButtonProps(
    'supportCoreActivity',
    'Was this supporting R&D activity undertaken for the dominant purpose of supporting the core R&D activity?',
    ['SupportingActivityDominantPurposeWhat', 'SupportingActivityDominantPurposeWhen', 'SupportingActivityDominantPurpose']);

const relatedGoodsAndServices: FieldModel<SupportingActivityItemModel> = yesNoRadioButtonProps(
    'relatedGoodsAndServices',
    'Did this supporting R&D activity produce a good or a service, or is it directly related to producing a good or a service?',
    ['SupportingActivityGoodOrService']);

const actionsStepsCompleted: FieldModel<SupportingActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Briefly describe this supporting R&D activity',
    contentKey: ['actionsStepsCompleted'],
    name: 'actionsStepsCompleted',
    componentProps: {
        maxCharacters: 4000,
    },
};

const activityPerformedBy: FieldModel<SupportingActivityItemModel> = {
    component: 'radiobuttonGroup',
    label: 'Who will this supporting R&D activity be performed by?',
    name: 'activityPerformedBy',
    maxWidth: '22rem',
    contentKey: ['AFIActivitiesPerformedByCompany', 'AFIActivitiesPerformedByServiceProvider', 'AFIActivitiesPerformedByCooperativeResearchCentre'],
    componentProps: {
        name: 'activityPerformedBy',
        radioOptions: [
            {
                id: 'activityPerformedBy-OnlyTheRnDCompany',
                label: AFIActivitiesPerformedByOptionsEnumDesc['OnlyTheRnDCompany'],
                value: 'OnlyTheRnDCompany',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['OnlyTheRnDCompany'],
            },
            {
                id: 'activityPerformedBy-RnDCompanyAndOthers',
                label: AFIActivitiesPerformedByOptionsEnumDesc['RnDCompanyAndOthers'],
                value: 'RnDCompanyAndOthers',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['RnDCompanyAndOthers'],
            },
            {
                id: 'activityPerformedBy-EitherASubsidiary',
                label: AFIActivitiesPerformedByOptionsEnumDesc['EitherASubsidiary'],
                value: 'EitherASubsidiary',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['EitherASubsidiary'],
            },
            {
                id: 'activityPerformedBy-OnlyOthers',
                label: AFIActivitiesPerformedByOptionsEnumDesc['OnlyOthers'],
                value: 'OnlyOthers',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['OnlyOthers'],
            },
        ],
    },
};

const isEntityWhoWillConductSupportingConnected = yesNoRadioButtonProps(
    'isEntityWhoWillConductSupportingConnected',
    'Is the entity who will conduct this supporting R&D activity a connected or affiliated entity?',
    []);

const isSupportingActivityConductedUnderAgreementOutsideAus = yesNoRadioButtonProps(
    'isSupportingActivityConductedUnderAgreementOutsideAus',
    'Is the supporting R&D activity being conducted under an agreement the R&D entity has with a connected or affiliated entity who is located outside of Australia? ',
    []);

const activityConductedBy: FieldModel<SupportingActivityItemModel> = {
    component: 'radiobuttonGroup',
    label: 'Will you conduct this supporting R&D activity as the head entity of a consolidated group or MEC group, or will this supporting R&D activity be conducted by a subsidiary?',
    name: 'activityConductedBy',
    componentProps: {
        name: 'activityConductedBy',
        radioOptions: [
            {
                id: 'activityConductedBy-mecGroup',
                label: ActivityConductedByTypeEnumDesc['ConsolidatedMECGroup'],
                value: 'ConsolidatedMECGroup',
                tooltip: ActivityConductedByTypeEnumDesc['ConsolidatedMECGroup'],
                maxWidth: '812px',
            },
            {
                id: 'activityConductedBy-subsidiary',
                label: ActivityConductedByTypeEnumDesc['Subsidiary'],
                value: 'Subsidiary',
                tooltip: ActivityConductedByTypeEnumDesc['Subsidiary'],
                maxWidth: '812px',
            },
        ],
    },
};

const subsidiaryConductingActivity: FieldModel<SupportingActivityItemModel> = {
    component: 'abnAcnLookup',
    label: `What is the ABN, ACN or ARBN of the subsidiary conducting this supporting R&D activity?`,
    name: 'subsidiaryConductingActivity',
};

const nameRNDEntityPerformingSupportingActivity: FieldModel<SupportingActivityItemModel> = {
    component: 'textInput',
    label: 'Name of R&D entity performing this supporting R&D activity',
    name: 'nameRNDEntityPerformingSupportingActivity',
};

const supportForCoreActivities: FieldModel<SupportingActivityItemModel> = {
    component: 'textAreaInput',
    label: 'How did this supporting R&D activity directly support the core R&D activities?',
    contentKey: ['AFISupportForCoreActivities'],
    name: 'supportForCoreActivities',
    componentProps: {
        maxCharacters: 4000,
    },
};

const totalExpenditure: FieldModel<SupportingActivityItemModel> = {
    component: 'currencyInput',
    label: 'For the selected income period, what was the estimated expenditure for this supporting R&D activity?',
    name: 'totalExpenditure',
    contentKey: ['SupportingActivityTotalExpenditure'],
    maxWidth: '352px',
    // Allowing 15 digits plus 4 comma separators as max input length
    maxLength: 19,
};

const explainWhyHowSupportingActivityUndertaken: FieldModel<SupportingActivityItemModel> = {
    component: 'textAreaInput',
    label: `Please explain why and how this supporting R&D activity was undertaken for the dominant purpose of the core R&D activity.`,
    name: 'explainWhyHowSupportingActivityUndertaken',
    contentKey: ['AFIExplainWhyHowSupportingActivityUndertaken'],
    componentProps: {
        maxCharacters: 4000,
    },
};

const evidenceAvailable: FieldModel<SupportingActivityItemModel> = {
    component: 'textAreaInput',
    label: `What evidence did the company keep about this supporting R&D activity?`,
    name: 'evidenceAvailable',
    contentKey: ['SupportingActivityAvailableEvidence'],
    componentProps: {
        maxCharacters: 4000,
    },
};

const supportingActivities: NestedListDetailsProp<SupportingActivityItemModel> = {
    apiEndpoint: '/supporting-activities',
    name: 'supportingActivities',
    title: 'Supporting R&D activity',
    contentKey: 'AFISuppActivityAppGuidanceText',
    deleteButtonTitle: 'Delete activity',
    // tslint:disable-next-line: max-line-length
    deleteDialogBodyText: 'Are you sure you want to delete this supporting R&D activity?<br>Deleting the supporting will remove it from all linked core R&D activities.',
    fields: {
        isIntentionPreviouslyRegisteredSupportingRNDActivityApplication,
        name,
        actionsStepsCompleted,
        otherOrganisation,
        activityRelations: activityRelations as any,
        supportForCoreActivities,
        activityPerformedBy,
        nameRNDEntityPerformingSupportingActivity,
        isEntityWhoWillConductSupportingConnected,
        isSupportingActivityConductedUnderAgreementOutsideAus,
        activityConductedBy,
        subsidiaryConductingActivity,
        otherOrganisationName,
        briefDescription,
        duration,
        totalExpenditure,
        excluded,
        relatedGoodsAndServices,
        supportCoreActivity,
        explainWhyHowSupportingActivityUndertaken,
        evidenceAvailable,
    },
    initialPageValues: {
        isIntentionPreviouslyRegisteredSupportingRNDActivityApplication: undefined,
        name: '',
        actionsStepsCompleted: undefined,
        otherOrganisation: undefined,
        activityRelations: [],
        coreActivities: [],
        supportForCoreActivities: undefined,
        activityPerformedBy: undefined,
        nameRNDEntityPerformingSupportingActivity: '',
        isEntityWhoWillConductSupportingConnected: undefined,
        isSupportingActivityConductedUnderAgreementOutsideAus: undefined,
        activityConductedBy: undefined,
        subsidiaryConductingActivity: undefined,
        otherOrganisationName: '',
        briefDescription: '',
        duration: {
            endMonth: undefined,
            endYear: undefined,
            startMonth: undefined,
            startYear: undefined,
        },
        totalExpenditure: undefined,
        excluded: undefined,
        relatedGoodsAndServices: undefined,
        supportCoreActivity: undefined,
        explainWhyHowSupportingActivityUndertaken: '',
        evidenceAvailable: undefined,
        allOtherSubActivitiesForThisApp: [],
    },
};

export default supportingActivities;
